import React, { useState, useRef, useEffect } from 'react';
import { X, Wand2, Send, Replace, Loader2, RotateCcw, Paperclip, Download } from 'lucide-react';
import { neonQuery } from '../services/api';
import { getCurrentUser, getAccessToken } from '../services/auth';

const QUALITY_PRESETS = ['low', 'medium', 'high'];
const ORIGINAL_MAX_EDGE = 2048;
const ORIGINAL_QUALITY = 0.9;
const THUMB_MAX_EDGE = 480;
const THUMB_QUALITY = 0.78;

// Prefill presets. `attachLogos` slugs match the server-side LOGO_BRAND_ASSET_IDS map.
const PROMPT_PRESETS = [
  {
    key: 'logo-cc',
    label: 'Add C&C logo',
    brandHint: 'crab-and-cruise',
    attachLogos: ['cnc'],
    prompt: "Composite the Crab & Cruise logo (second reference image) onto this photo in a tasteful, well-placed spot — bottom corner or low-contrast area that doesn't cover faces. Preserve the logo's proportions and colors. Don't alter the people, scene, or composition; only add the logo.",
  },
  {
    key: 'logo-cp',
    label: 'Add Crab Place logo',
    brandHint: 'crab-place',
    attachLogos: ['crab-place'],
    prompt: "Composite the Crab Place logo (second reference image) onto this photo in a tasteful, well-placed spot — bottom corner or low-contrast area that doesn't cover faces. Preserve the logo's proportions and colors. Don't alter the people, scene, or composition; only add the logo.",
  },
  {
    key: 'brighten',
    label: 'Brighten',
    attachLogos: [],
    prompt: 'Brighten the image overall, lift shadows, and add a touch more warmth without blowing out the highlights. Keep colors natural.',
  },
  {
    key: 'sharpen',
    label: 'Sharpen',
    attachLogos: [],
    prompt: 'Sharpen the image, increase clarity on the main subject, and improve perceived detail without introducing halos or oversharpening artifacts.',
  },
  {
    key: 'remove-bg',
    label: 'Remove background',
    attachLogos: [],
    prompt: 'Remove the background entirely so only the main subject remains on a transparent (or plain white) background. Preserve fine edges (hair, fur, fingers, soft edges).',
  },
];

// ⚠️ TEXT OVERLAY IS NOT AN AI PRESET AND MUST NOT BECOME ONE AGAIN.
// It used to be the prompt "add this text, don't alter the photo", which the
// image model cannot honour — it regenerates the whole picture, so the crabs
// move and it invents its own Crab Place logo where it sees one in the source.
// These run through `media-text-overlay`, which composites real Montserrat
// outlines onto the real bytes. Greg, 2026-09-15: *"I just want that text
// overlay nothing else."*
const OVERLAY_PRESETS = [
  { key: 'boxes-happy', label: 'Our Boxes Make People Happy', text: 'Our Boxes Make People Happy' },
];

const OVERLAY_POSITIONS = [
  { value: 'auto', label: 'Auto (quietest corner)' },
  { value: 'top-left', label: 'Top left' },
  { value: 'top-center', label: 'Top centre' },
  { value: 'top-right', label: 'Top right' },
  { value: 'bottom-left', label: 'Bottom left' },
  { value: 'bottom-center', label: 'Bottom centre' },
  { value: 'bottom-right', label: 'Bottom right' },
];

// Matches STYLES in netlify/functions/lib/text-overlay.js. Banner is the
// default because plain type alone looked unfinished (Greg, 2026-10-21).
const OVERLAY_STYLES = [
  { value: 'banner', label: 'Banner', hint: 'Dark fade across the bottom, orange rule, white + orange words' },
  { value: 'panel', label: 'Panel', hint: 'Navy card with an orange stripe' },
  { value: 'sticker', label: 'Sticker', hint: 'Tilted orange tag' },
  { value: 'plain', label: 'Plain', hint: 'Words only, no background' },
];

const OVERLAY_SIZES = [
  { value: 's', label: 'S' },
  { value: 'm', label: 'M' },
  { value: 'l', label: 'L' },
  { value: 'xl', label: 'XL' },
];

const OVERLAY_COLORS = [
  { value: 'brand', label: 'Brand (navy + orange)' },
  { value: 'navy', label: 'Navy' },
  { value: 'orange', label: 'Orange' },
  { value: 'white', label: 'White' },
];

const LOGO_LABELS = {
  'cnc': 'Crab & Cruise logo',
  'crab-and-cruise': 'Crab & Cruise logo',
  'crab-place': 'Crab Place logo',
};

function pollDelay(attempt) {
  // 1.5s for the first 3 attempts, then 3s steady. Fast feedback up front,
  // then back off so we don't hammer Neon for the long tail.
  if (attempt < 3) return 1500;
  return 3000;
}

function sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }

// Fetch via our own r2-image proxy (CORS:*) so canvas can read bytes without
// tainting. Then resize + JPEG-encode for media-library-upload.
async function urlToResizedBase64(url, maxEdge, quality) {
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.crossOrigin = 'anonymous';
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Could not load generated image for save'));
    el.src = url;
  });
  const naturalWidth = img.naturalWidth;
  const naturalHeight = img.naturalHeight;
  const scale = Math.min(1, maxEdge / Math.max(naturalWidth, naturalHeight));
  const w = Math.max(1, Math.round(naturalWidth * scale));
  const h = Math.max(1, Math.round(naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);
  const jpeg = canvas.toDataURL('image/jpeg', quality);
  const comma = jpeg.indexOf(',');
  return { base64: jpeg.slice(comma + 1), naturalWidth, naturalHeight };
}

export default function MediaAIEditModal({ item, onClose, onSaved }) {
  const user = getCurrentUser();
  const sourceUrl = `/.netlify/functions/r2-image?key=${encodeURIComponent(item.r2_original_key)}`;
  // history entries:
  //   { kind: 'source', url }
  //   { kind: 'generated', url, key, prompt }
  //   { kind: 'message', text, prompt }   ← when GPT replies text-only
  const [history, setHistory] = useState([{ kind: 'source', url: sourceUrl }]);
  const [prompt, setPrompt] = useState('');
  const [quality, setQuality] = useState('medium');
  const [generating, setGenerating] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [attachLogos, setAttachLogos] = useState([]);
  const [overlayText, setOverlayText] = useState('');
  const [overlayPos, setOverlayPos] = useState('auto');
  const [overlayColor, setOverlayColor] = useState('brand');
  const [overlayShadow, setOverlayShadow] = useState(true);
  const [overlayStyle, setOverlayStyle] = useState('banner');
  const [overlaySize, setOverlaySize] = useState('m');
  const [overlayRule, setOverlayRule] = useState(true);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [busy, setBusy] = useState(null);            // null | 'downloading' | 'saving' (blocks other actions)
  const [saveToLibrary, setSaveToLibrary] = useState(false);
  const [replaceMode, setReplaceMode] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [error, setError] = useState(null);
  const promptRef = useRef(null);
  const threadRef = useRef(null);
  const downloadRef = useRef(null);

  useEffect(() => {
    if (!downloadOpen) return;
    const onDocClick = (e) => { if (downloadRef.current && !downloadRef.current.contains(e.target)) setDownloadOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [downloadOpen]);

  // Reorder the prefill presets so the brand matching the source asset comes first.
  const orderedPresets = (() => {
    const itemBrand = item.brand || 'crab-place';
    return [...PROMPT_PRESETS].sort((a, b) => {
      const aMatch = a.brandHint === itemBrand ? 0 : (a.brandHint ? 1 : 2);
      const bMatch = b.brandHint === itemBrand ? 0 : (b.brandHint ? 1 : 2);
      return aMatch - bMatch;
    });
  })();

  useEffect(() => { promptRef.current?.focus(); }, []);
  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [history.length, error]);

  const generatedTurns = history.filter((h) => h.kind === 'generated');
  const hasGenerated = generatedTurns.length > 0;
  const lastGenerated = generatedTurns[generatedTurns.length - 1] || null;

  const applyPreset = (preset) => {
    setPrompt(preset.prompt);
    setAttachLogos(preset.attachLogos || []);
    setTimeout(() => promptRef.current?.focus(), 0);
  };

  // Deterministic text overlay. No model, no job row, no polling — the server
  // composites and answers in about a second, so this is a plain await.
  //
  // `over.restyle` REPLACES the last turn instead of stacking on it: it renders
  // from that turn's own source, so flipping Size or Style five times leaves
  // one layer of text, not five piled on top of each other.
  const runOverlay = async (text, over = {}) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || generating || !!busy) return;
    const last = history[history.length - 1];
    const restyle = !!over.restyle && !!last?.overlay;
    setError(null);
    setGenerating(true);
    setStatusMessage(restyle ? 'Updating the text…' : 'Adding the text…');
    try {
      // Otherwise stack onto the latest turn, so an overlay can follow an AI
      // edit and a second overlay follows the first — same rule as the AI path.
      const sourceKey = restyle ? last.overlay.baseKey : (lastGenerated?.key || item.r2_original_key);
      const settings = {
        position: over.position ?? overlayPos,
        color: over.color ?? overlayColor,
        shadow: over.shadow ?? overlayShadow,
        style: over.style ?? overlayStyle,
        size: over.size ?? overlaySize,
        rule: over.rule ?? overlayRule,
      };
      const res = await fetch('/.netlify/functions/media-text-overlay', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
        },
        body: JSON.stringify({
          sourceKey,
          text: trimmed,
          ...settings,
          filename: `${(item.suggested_name || 'image').slice(0, 40)}-text`,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Overlay failed (${res.status})`);

      const entry = {
        kind: 'generated',
        url: data.url,
        key: data.key,
        prompt: `Text overlay: "${trimmed}"`,
        placement: data.placement,
        overlay: { text: trimmed, baseKey: sourceKey },
      };
      setHistory((h) => (restyle ? [...h.slice(0, -1), entry] : [...h, entry]));
      setOverlayOpen(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
      setStatusMessage('');
    }
  };

  // A look control changed. Remember it, and if the latest turn is text, redo
  // that text in the new look right away.
  const restyleOverlay = (change) => {
    if ('style' in change) setOverlayStyle(change.style);
    if ('size' in change) setOverlaySize(change.size);
    if ('rule' in change) setOverlayRule(change.rule);
    if ('position' in change) setOverlayPos(change.position);
    const last = history[history.length - 1];
    if (last?.overlay) runOverlay(last.overlay.text, { ...change, restyle: true });
  };

  const onGenerate = async () => {
    const trimmed = prompt.trim();
    if (!trimmed || generating || !!busy) return;
    setError(null);
    setGenerating(true);
    setStatusMessage('Queueing job…');
    try {
      const sourceKey = lastGenerated?.key || item.r2_original_key;
      const jobId = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `job-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

      // Fire-and-poll: background function returns 202 immediately. We don't
      // rely on its response body; the job row in Neon is the source of truth.
      fetch('/.netlify/functions/media-ai-edit-background', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobId,
          prompt: trimmed,
          sourceKey,
          quality,
          attachLogos,
          filename: `${(item.suggested_name || 'edit').slice(0, 40)}-edit`,
        }),
      }).catch((e) => console.warn('background kickoff fetch failed:', e.message));

      // Poll until status flips to done or failed. Cap at ~10 min wall-clock
      // (background function ceiling is 15 min; OpenAI rarely exceeds 3 min).
      const startedAt = Date.now();
      let attempt = 0;
      let row = null;
      while (Date.now() - startedAt < 10 * 60 * 1000) {
        await sleep(pollDelay(attempt));
        attempt += 1;
        try {
          const statusRes = await fetch(`/.netlify/functions/media-ai-edit-status?jobId=${encodeURIComponent(jobId)}`);
          const data = await statusRes.json();
          if (data.status === 'not_found') {
            setStatusMessage('Waiting for server…');
            continue;
          }
          if (data.status_message) setStatusMessage(data.status_message);
          if (data.status === 'done' || data.status === 'failed') { row = data; break; }
          // 'pending' or 'running' — keep polling
        } catch (e) {
          // Transient network error — keep trying, but surface eventually
          if (attempt > 5) console.warn('status poll error:', e.message);
        }
      }

      if (!row) throw new Error('Generation timed out after 10 minutes — try again or simplify the prompt.');
      if (row.status === 'failed') throw new Error(row.error_message || 'Generation failed');

      if (row.result_url) {
        setHistory((h) => [...h, { kind: 'generated', url: row.result_url, key: row.result_key, prompt: trimmed, attachedLogos: attachLogos.slice() }]);
      } else if (row.result_text) {
        setHistory((h) => [...h, { kind: 'message', text: row.result_text, prompt: trimmed }]);
      } else {
        throw new Error('Server finished but returned neither image nor message');
      }
      setPrompt('');
      setAttachLogos([]);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
      setStatusMessage('');
    }
  };

  const onUndoLast = () => {
    setHistory((h) => h.slice(0, -1));
    setError(null);
  };

  // Download sizes mirror the regular Detail toolbar. We don't know the master
  // dimensions of the AI-edited image up front, so we don't grey out sizes
  // ≥ master — every option always works (r2-resize just stops at the source's
  // long edge if smaller).
  const DOWNLOAD_SIZES = [
    { label: 'Original', edge: null },
    { label: 'Large', edge: 1600 },
    { label: 'Medium', edge: 1200 },
    { label: 'Web', edge: 800 },
    { label: 'Email', edge: 600 },
    { label: 'Thumb', edge: 400 },
  ];

  // Trigger a browser download via existing r2-fetch (Original) / r2-resize
  // (resized variants). Same plumbing as the Detail toolbar's Download dropdown.
  const triggerDownload = (key, size, turnIndex) => {
    const baseName = (item.suggested_name || 'image').replace(/[^a-z0-9-]/gi, '-').toLowerCase();
    if (size.edge == null) {
      const filename = `${baseName}-ai-edit-t${turnIndex}.png`;
      const a = document.createElement('a');
      a.href = `/.netlify/functions/r2-fetch?key=${encodeURIComponent(key)}&filename=${encodeURIComponent(filename)}`;
      a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
    } else {
      const filename = `${baseName}-ai-edit-t${turnIndex}-${size.edge}px.jpg`;
      const a = document.createElement('a');
      a.href = `/.netlify/functions/r2-resize?key=${encodeURIComponent(key)}&edge=${size.edge}&filename=${encodeURIComponent(filename)}`;
      a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
    }
  };

  // Save full-quality AI result to the Media Library + optional archive of the
  // original. Same flow as the previous onSave; now gated by the
  // saveToLibrary checkbox instead of being the primary CTA.
  const saveResultToLibrary = async () => {
    if (!lastGenerated) return;
    // Read through r2-resize, not the turn's r2-image URL: a result over 4 MB
    // (text on a full-size phone photo) makes r2-image 302 to presigned R2,
    // which has no CORS for us, so the crossOrigin load failed with "Could not
    // load generated image for save" (Greg, Oct 29, 2026).
    const saveUrl = (edge) => (lastGenerated.key
      ? `/.netlify/functions/r2-resize?key=${encodeURIComponent(lastGenerated.key)}&edge=${edge}`
      : lastGenerated.url);
    const [orig, thumb] = await Promise.all([
      urlToResizedBase64(saveUrl(ORIGINAL_MAX_EDGE), ORIGINAL_MAX_EDGE, ORIGINAL_QUALITY),
      urlToResizedBase64(saveUrl(THUMB_MAX_EDGE), THUMB_MAX_EDGE, THUMB_QUALITY),
    ]);
    const baseName = (item.suggested_name || 'image').replace(/[^a-z0-9-]/gi, '-').toLowerCase();
    const filename = `${baseName}-ai-edit.jpg`;

    const uploadRes = await fetch('/.netlify/functions/media-library-upload', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename,
        contentType: 'image/jpeg',
        brand: item.brand || 'crab-place',
        originalBase64: orig.base64,
        thumbBase64: thumb.base64,
        width: orig.naturalWidth,
        height: orig.naturalHeight,
        admin_id: user?.id,
        extraTags: ['ai-edited', `edited-from-${item.id}`],
      }),
    });
    const uploadData = await uploadRes.json();
    if (!uploadRes.ok) throw new Error(uploadData.error || 'Upload failed');

    // Overwrite Claude's classify-note with the prompt chain so the audit trail
    // shows what was actually requested.
    const prompts = history.filter((h) => h.prompt).map((h) => h.prompt);
    const noteLines = [`AI-edited from "${item.suggested_name || `asset #${item.id}`}".`];
    if (prompts.length) noteLines.push('Prompt chain: ' + prompts.map((p) => `"${p}"`).join(' → '));
    const newId = uploadData.row?.id;
    if (newId) {
      try {
        await neonQuery('media_assets_update', {
          id: newId,
          admin_id: user?.id,
          fields: { notes: noteLines.join('\n') },
        });
      } catch (e) { console.warn('media_assets_update (notes) failed:', e.message); }
    }

    if (replaceMode) {
      try {
        await neonQuery('media_assets_archive_toggle', {
          id: item.id,
          admin_id: user?.id,
          archived: true,
        });
      } catch (e) { console.warn('archive original failed:', e.message); }
    }
    return uploadData.row;
  };

  // Composite handler: download triggers the file save instantly; if the
  // "Also save to Media Library" checkbox is on, fire the library-upload too
  // and re-render the Media Library on close.
  const onDownload = async (size) => {
    if (!lastGenerated || generating || !!busy) return;
    setError(null);
    setDownloadOpen(false);
    const turnIndex = generatedTurns.length;
    try {
      triggerDownload(lastGenerated.key, size, turnIndex);
      if (saveToLibrary) {
        setBusy('saving');
        const row = await saveResultToLibrary();
        if (onSaved) onSaved(row, { replaced: replaceMode });
        onClose();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, maxWidth: 1280, width: '100%', maxHeight: '94vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 22px', borderBottom: '1px solid var(--gray-200)', background: 'linear-gradient(180deg,#fafafa,#f1f1f3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Wand2 size={18} style={{ color: 'var(--orange)' }} />
            <div>
              <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15 }}>Edit with AI</div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', fontFamily: 'monospace' }}>{item.suggested_name}</div>
            </div>
          </div>
          <button onClick={onClose} title="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', padding: 6, display: 'flex' }}><X size={18} /></button>
        </div>

        <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
          {/* Side-by-side image panes */}
          <div style={{ flex: 1.4, background: '#0b0b0b', display: 'flex', padding: 14, gap: 10, minHeight: 0 }}>
            <ImagePane label="Original" url={history[0].url} />
            <ImagePane
              label={hasGenerated ? `Edit · turn ${generatedTurns.length}` : (generating ? 'Generating…' : 'Result preview')}
              url={lastGenerated?.url || null}
              loading={generating}
              placeholder={!hasGenerated && !generating ? 'Your edit will appear here' : null}
            />
          </div>

          {/* Right column: thread + controls */}
          <div style={{ flex: 1, padding: 18, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 340, maxWidth: 460, borderLeft: '1px solid var(--gray-200)' }}>
            <div ref={threadRef} style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingRight: 4 }}>
              {history.length === 1 ? (
                <div style={{ background: '#f5f5f7', padding: 12, borderRadius: 6, fontSize: 12.5, color: '#424245', lineHeight: 1.45 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--navy)' }}>How this works</div>
                  Describe what to change. Each <strong>Refine</strong> builds on the previous result, so you can iterate until it's right. Then <strong>Save as new</strong> drops the final edit into the Media Library beside the original.
                </div>
              ) : null}
              {history.filter((h) => h.kind !== 'source').map((h, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ fontSize: 10, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>Turn {i + 1}</div>
                  <div style={{ background: 'var(--orange)', color: '#fff', padding: '6px 10px', borderRadius: 8, fontSize: 13, alignSelf: 'flex-end', maxWidth: '92%', wordBreak: 'break-word' }}>{h.prompt}</div>
                  {h.kind === 'message' ? (
                    <div style={{ background: '#fffbe6', padding: 8, borderRadius: 6, fontSize: 12, color: '#6b5800' }}>{h.text}</div>
                  ) : (
                    <div style={{ background: '#f5f5f7', padding: '6px 10px', borderRadius: 6, fontSize: 12, color: '#424245', alignSelf: 'flex-start' }}>✓ Generated</div>
                  )}
                </div>
              ))}
              {generating && statusMessage ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#eef4ff', color: '#1b4894', padding: '6px 10px', borderRadius: 6, fontSize: 12 }}>
                  <Loader2 size={12} className="spin" /> {statusMessage}
                </div>
              ) : null}
              {error ? <div style={{ background: '#ffe4e4', color: '#a31818', padding: 8, borderRadius: 6, fontSize: 12 }}>{error}</div> : null}
            </div>

            <div>
              {/* Text overlay — deterministic, no model. Kept visually apart
                  from the AI presets below because it behaves differently:
                  it runs immediately and leaves the photo alone. */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--navy)', letterSpacing: '.04em', textTransform: 'uppercase', marginRight: 2 }}>
                  Text on photo
                </span>
                {OVERLAY_PRESETS.map((p) => (
                  <button key={p.key} type="button" onClick={() => runOverlay(p.text)} disabled={generating || !!busy}
                    title="Composites real brand type onto the photo. The photo itself is not changed."
                    style={{ background: 'var(--navy)', color: '#fff', border: '1px solid var(--navy)', borderRadius: 999, padding: '3px 10px', fontSize: 11, cursor: generating || busy ? 'not-allowed' : 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {p.label}
                  </button>
                ))}
                <button type="button" onClick={() => setOverlayOpen((v) => !v)} disabled={generating || !!busy}
                  style={{ background: '#fff', color: 'var(--navy)', border: '1px solid var(--navy)', borderRadius: 999, padding: '3px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {overlayOpen ? 'Close' : 'Other text…'}
                </button>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500, #86868b)', letterSpacing: '.04em', textTransform: 'uppercase', marginRight: 2 }}>
                  Style
                </span>
                {OVERLAY_STYLES.map((o) => {
                  const on = overlayStyle === o.value;
                  return (
                    <button key={o.value} type="button" onClick={() => restyleOverlay({ style: o.value })} disabled={generating || !!busy}
                      title={o.hint}
                      style={{ background: on ? 'var(--orange)' : '#fff', color: on ? '#fff' : '#424245', border: `1px solid ${on ? 'var(--orange)' : 'var(--gray-200)'}`, borderRadius: 999, padding: '2px 9px', fontSize: 11, cursor: 'pointer', fontWeight: on ? 600 : 500, whiteSpace: 'nowrap' }}>
                      {o.label}
                    </button>
                  );
                })}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500, #86868b)', letterSpacing: '.04em', textTransform: 'uppercase', marginRight: 2 }}>
                  Size
                </span>
                {OVERLAY_SIZES.map((o) => {
                  const on = overlaySize === o.value;
                  return (
                    <button key={o.value} type="button" onClick={() => restyleOverlay({ size: o.value })} disabled={generating || !!busy}
                      style={{ background: on ? 'var(--orange)' : '#fff', color: on ? '#fff' : '#424245', border: `1px solid ${on ? 'var(--orange)' : 'var(--gray-200)'}`, borderRadius: 999, padding: '2px 9px', fontSize: 11, cursor: 'pointer', fontWeight: on ? 600 : 500, minWidth: 28 }}>
                      {o.label}
                    </button>
                  );
                })}
                <select value={overlayPos} onChange={(e) => restyleOverlay({ position: e.target.value })} disabled={generating || !!busy}
                  title="Where the text sits"
                  style={{ fontSize: 11, padding: '2px 6px', border: '1px solid var(--gray-200)', borderRadius: 6, marginLeft: 6 }}>
                  {OVERLAY_POSITIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {overlayStyle === 'banner' && (
                  <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', marginLeft: 6 }}
                    title="The short orange line above the words">
                    <input type="checkbox" checked={overlayRule} onChange={(e) => restyleOverlay({ rule: e.target.checked })} disabled={generating || !!busy} />
                    Orange line
                  </label>
                )}
              </div>

              {overlayOpen && (
                <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: 8, marginBottom: 8, background: '#fafafa' }}>
                  <input
                    type="text"
                    value={overlayText}
                    onChange={(e) => setOverlayText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runOverlay(overlayText); } }}
                    placeholder="Type the words to put on the photo"
                    maxLength={200}
                    style={{ width: '100%', padding: '6px 8px', fontSize: 13, border: '1px solid var(--gray-200)', borderRadius: 6, marginBottom: 6 }}
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                    <select value={overlayColor} onChange={(e) => setOverlayColor(e.target.value)}
                      style={{ fontSize: 11, padding: '3px 6px', border: '1px solid var(--gray-200)', borderRadius: 6 }}>
                      {OVERLAY_COLORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {overlayStyle === 'plain' && (
                      <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                        <input type="checkbox" checked={overlayShadow} onChange={(e) => setOverlayShadow(e.target.checked)} />
                        Shadow
                      </label>
                    )}
                    <button type="button" onClick={() => runOverlay(overlayText)}
                      disabled={generating || !!busy || !overlayText.trim()}
                      style={{ marginLeft: 'auto', background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 11, fontWeight: 600, cursor: overlayText.trim() ? 'pointer' : 'not-allowed' }}>
                      Add text
                    </button>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--gray-500, #86868b)', marginTop: 6 }}>
                    Montserrat Bold, composited onto the photo. The photo is not regenerated, so nothing in it moves.
                  </div>
                </div>
              )}

              {/* Prefill presets — brand-matching preset is first. These DO go
                  to the image model, so they can restyle the whole picture. */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500, #86868b)', letterSpacing: '.04em', textTransform: 'uppercase', marginRight: 2 }}>
                  AI edit
                </span>
                {orderedPresets.map((p) => (
                  <button key={p.key} type="button" onClick={() => applyPreset(p)} disabled={generating || !!busy}
                    title={p.prompt}
                    style={{ background: '#fff', color: '#424245', border: '1px solid var(--gray-200)', borderRadius: 999, padding: '3px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {p.label}
                  </button>
                ))}
              </div>

              {/* Attached-logo badges — show when a preset attached a brand asset */}
              {attachLogos.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                  {attachLogos.map((slug) => (
                    <span key={slug} style={{ background: '#fff7eb', color: '#8a5a00', border: '1px solid #f0d28a', borderRadius: 4, padding: '3px 4px 3px 8px', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Paperclip size={11} /> {LOGO_LABELS[slug] || slug}
                      <button onClick={() => setAttachLogos((arr) => arr.filter((x) => x !== slug))} disabled={generating || !!busy}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 1, display: 'inline-flex', color: '#8a5a00' }}>
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}

              <textarea
                ref={promptRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onGenerate(); } }}
                rows={3}
                placeholder={hasGenerated
                  ? "Refine further — e.g. 'make the sky more dramatic'"
                  : "Describe the change — e.g. 'add a Crab Place box on the bench next to him'"}
                style={{ width: '100%', boxSizing: 'border-box', padding: 10, border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }}
                disabled={generating || !!busy}
              />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, gap: 8, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--gray-500)' }}>
                  Quality:
                  {QUALITY_PRESETS.map((q) => (
                    <button key={q} onClick={() => setQuality(q)} disabled={generating || !!busy}
                      style={{ background: quality === q ? 'var(--navy)' : '#fff', color: quality === q ? '#fff' : '#424245', border: '1px solid var(--gray-200)', padding: '3px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize' }}>
                      {q}
                    </button>
                  ))}
                </div>
                <button onClick={onGenerate} disabled={!prompt.trim() || generating || !!busy}
                  style={{ background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', fontWeight: 600, fontSize: 13, cursor: (!prompt.trim() || generating || !!busy) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: (!prompt.trim() || generating || !!busy) ? 0.55 : 1 }}>
                  {generating ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
                  {generating ? 'Generating…' : (hasGenerated ? 'Refine' : 'Generate')}
                </button>
              </div>

              {hasGenerated ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--gray-200)' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={onUndoLast} disabled={!!busy || generating} title="Drop the last result and refine from the previous turn"
                      style={{ flex: '0 0 auto', background: '#fff', color: '#424245', border: '1px solid var(--gray-200)', borderRadius: 6, padding: '8px 10px', fontSize: 12, cursor: (!!busy || generating) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <RotateCcw size={12} /> Undo last
                    </button>
                    <div ref={downloadRef} style={{ flex: 1, position: 'relative' }}>
                      <button onClick={() => setDownloadOpen((v) => !v)} disabled={!!busy || generating}
                        style={{ width: '100%', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 12px', fontWeight: 600, fontSize: 13, cursor: (!!busy || generating) ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        {busy === 'saving' ? <Loader2 size={14} className="spin" /> : <Download size={14} />}
                        {busy === 'saving' ? 'Saving to library…' : 'Download'}
                      </button>
                      {downloadOpen ? (
                        <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: 6, background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6, padding: 6, boxShadow: '0 6px 18px rgba(0,0,0,0.15)', zIndex: 20 }}>
                          <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5, padding: '4px 8px 6px', fontWeight: 600 }}>Download size</div>
                          {DOWNLOAD_SIZES.map((s) => {
                            const subtitle = s.edge == null ? 'PNG, full resolution' : `${s.edge}px long edge · JPEG`;
                            return (
                              <button key={s.label} onClick={() => onDownload(s)} disabled={!!busy}
                                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 8px', fontSize: 12, background: 'none', border: 'none', borderRadius: 4, cursor: !!busy ? 'wait' : 'pointer', color: '#1d1d1f' }}
                                onMouseEnter={(e) => { if (!busy) e.currentTarget.style.background = '#f5f5f7'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
                                <div style={{ fontWeight: 600 }}>{s.label}</div>
                                <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>{subtitle}</div>
                              </button>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#424245', cursor: 'pointer' }}>
                    <input type="checkbox" checked={saveToLibrary} onChange={(e) => { setSaveToLibrary(e.target.checked); if (!e.target.checked) setReplaceMode(false); }} />
                    Also save to Media Library
                  </label>
                  {saveToLibrary ? (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#424245', cursor: 'pointer', paddingLeft: 22 }}>
                      <input type="checkbox" checked={replaceMode} onChange={(e) => setReplaceMode(e.target.checked)} />
                      <Replace size={12} /> Archive the original after save
                    </label>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ImagePane({ label, url, loading, placeholder }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: '#000', borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, color: 'rgba(255,255,255,0.65)', padding: '6px 10px', background: 'rgba(255,255,255,0.06)', fontWeight: 600 }}>{label}</div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8, minHeight: 0 }}>
        {url ? <img src={url} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          : loading ? <Loader2 className="spin" size={32} color="#fff" />
          : <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, textAlign: 'center', padding: 20 }}>{placeholder}</div>}
      </div>
    </div>
  );
}
